import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportError } from 'helpers/error-logging';

import modifierGateway from './Modifiers.gateway';
import { modifierKeys } from './Modifiers.queryKeys';

const applySoldOut = (groups, { modifierId, soldOut }) =>
  (groups || []).map(group => ({
    ...group,
    modifiers: group.modifiers.map(modifier => (modifier.core.id === modifierId ? { ...modifier, soldOut } : modifier)),
  }));

export default function useModifierMutations({ venueId }) {
  const queryClient = useQueryClient();
  const groupsKey = modifierKeys.groups(venueId);
  const invalidateGroups = () => queryClient.invalidateQueries({ queryKey: groupsKey });

  const createGroup = useMutation({
    mutationFn: group => modifierGateway.createModifierGroup(venueId, group),
    onError: error => reportError('Error creating SKU modifier group', error),
    onSettled: invalidateGroups,
  });

  // Optimistic: the toggle is a controlled checkbox, so the cache must flip immediately or
  // React snaps it back to the old value before the refetch lands (mirrors useSkuMutations).
  const setModifierSoldOut = useMutation({
    mutationFn: ({ modifierId, soldOut }) => modifierGateway.updateModifier(venueId, modifierId, { soldOut }),
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: groupsKey });
      const snapshot = queryClient.getQueryData(groupsKey);
      queryClient.setQueryData(groupsKey, current => applySoldOut(current, variables));
      return { snapshot };
    },
    onError: (error, variables, context) => {
      if (context) queryClient.setQueryData(groupsKey, context.snapshot);
      reportError('Error updating SKU modifier availability', error);
    },
    onSettled: invalidateGroups,
  });

  return { createGroup, setModifierSoldOut };
}
